import { FC, useEffect, useState } from "react";

interface RevealOverlayProps {
  active: boolean;
  /** Called while overlay is opaque — switch view behind it. */
  onSwitch: () => void;
  /** Called after overlay fades out — start widget animations. */
  onComplete: () => void;
}

export const RevealOverlay: FC<RevealOverlayProps> = ({ active, onSwitch, onComplete }) => {
  const [phase, setPhase] = useState<"idle" | "hold" | "fadeout" | "done">("idle");

  useEffect(() => {
    if (!active || phase !== "idle") return;
    setPhase("hold");

    // At 0.8s: logo is bright, overlay fully opaque — switch behind it
    const switchTimer = setTimeout(() => onSwitch(), 800);
    // At 1.6s: start fading out
    const fadeTimer = setTimeout(() => setPhase("fadeout"), 1600);
    // At 2.3s: fully gone — trigger widget animations
    const doneTimer = setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 2300);

    return () => {
      clearTimeout(switchTimer);
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active && phase === "done") setPhase("idle");
  }, [active, phase]);

  if (phase === "idle" || phase === "done") return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#170A10]"
      style={{
        transition: phase === "fadeout" ? "opacity 700ms ease-in-out" : undefined,
        opacity: phase === "fadeout" ? 0 : 1,
      }}
    >
      <div
        className="absolute rounded-full bg-sr-lemon-muted/20 blur-3xl"
        style={{
          width: 300,
          height: 300,
          animation: "sr-reveal-glow 2s ease-out forwards",
        }}
      />
      <div
        className="flex flex-col items-center gap-4"
        style={{ animation: "sr-reveal-logo-pulse 2s ease-in-out forwards" }}
      >
        <img
          src="/images/sugar-rush-logo.svg"
          alt="Sugar Rush"
          className="h-16 drop-shadow-[0_0_60px_var(--color-sr-lemon-muted)]"
        />
      </div>
    </div>
  );
};
